// src/api.js
import axios from "axios";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";

// ===== 1) Base URL (must be absolute, prefer HTTPS) =====
const DEFAULT_BASE_URL = "https://api.thirdplace.ing"; // <-- prod API

// Detect platform safely (web / ios / android)
const platform = Capacitor?.getPlatform?.() || "web";

// On native (ios/android), always use DEFAULT_BASE_URL.
// On web, allow env override (dev: localhost, prod: api.thirdplace.ing via REACT_APP_*)
const resolvedBase =
  platform === "ios" || platform === "android"
    ? DEFAULT_BASE_URL
    : process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_API_BASEURL || // alt var name
      DEFAULT_BASE_URL;

export const API_BASE_URL = String(resolvedBase).replace(/\/+$/, ""); // trim trailing slash

// ===== 2) Axios instance =====
export const api = axios.create({
  baseURL: API_BASE_URL || undefined,
  timeout: 20000,
  // withCredentials: false, // keep false unless using same-site cookies
});

// ===== 3) Attach Bearer token from Capacitor Preferences (fallback to web) =====
api.interceptors.request.use(async (config) => {
  try {
    // Avoid overwriting an explicit Authorization header
    const alreadyHasAuth =
      !!config.headers?.Authorization || !!config.headers?.authorization;

    if (!alreadyHasAuth) {
      let token = null;

      // Try Capacitor Preferences first (for iOS / Android / hybrid)
      try {
        const pref = await Preferences.get({ key: "auth_token" });
        if (pref?.value) token = pref.value;
      } catch {
        /* noop */
      }

      // Fallback to web storage
      if (!token && typeof window !== "undefined") {
        token =
          window.localStorage?.getItem("token") ||
          window.sessionStorage?.getItem("token") ||
          null;
      }

      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch {
    // ignore
  }

  // ----- FormData content-type handling -----
  const isFormData =
    typeof FormData !== "undefined" && config.data instanceof FormData;

  if (isFormData) {
    // Let Axios set the multipart boundary automatically
    if (config.headers) {
      delete config.headers["Content-Type"];
      if (config.headers.common) delete config.headers.common["Content-Type"];
      if (config.headers.post) delete config.headers.post["Content-Type"];
      if (config.headers.put) delete config.headers.put["Content-Type"];
    }
  } else {
    // Hint JSON for plain objects if not set
    if (config.headers && !config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }
  }

  return config;
});

// ===== 4) Debug: print the baseURL into the webview console =====
if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.log("[API_BASE_URL]", {
    platform,
    baseURL: API_BASE_URL || "(undefined)",
  });
}
